/* ─────────────────────────────────────────────────────────────────
   FRONTEND WEB DEVELOPMENT — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx
───────────────────────────────────────────────────────────────── */

export const FRONTEND_MODULES = [
  {
    module: 1,
    title: "HTML Fundamentals",
    emoji: "📄",
    topics: [
      {
        id: 1,
        topic: "HTML Basics",
        description: "Learn the fundamentals of HTML and understand how web pages are structured using semantic elements.",
        motivation: "Solid start! Every great website begins with clean, semantic HTML — you just built that foundation! 💪",
        resource: {
          name: "MDN — Basic HTML Syntax",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Structuring_content/Basic_HTML_syntax",
        },
        task: "Build a personal portfolio website using only HTML.",
        requirements: [
          "Create a valid HTML5 document",
          "Add a <header> with your name",
          "Create a navigation menu using <nav>",
          "Add an About Me section",
          "Create a Skills section using lists",
          "Add an Education section",
          "Create a Projects section",
          "Build a Contact section with basic contact information",
          "Add a footer",
          "Use semantic HTML elements (header, nav, main, section, article, footer)",
          "Ensure proper heading hierarchy (h1–h3)",
        ],
      },
      {
        id: 2,
        topic: "HTML Forms",
        description: "Learn how to collect user information using HTML forms and various input elements.",
        motivation: "You've mastered forms — the backbone of every login, registration, and checkout flow on the web! 🎯",
        resource: {
          name: "MDN — HTML Forms",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Extensions/Forms",
        },
        task: "Create a Student Registration Form.",
        requirements: [
          "Full Name field",
          "Email Address field",
          "Phone Number field",
          "Date of Birth field",
          "Gender selection",
          "Department (Dropdown)",
          "Skills (Checkboxes)",
          "Resume Upload",
          "Address (Textarea)",
          "Submit Button",
          "Reset Button",
          "Appropriate labels for every field",
          "Required field validation",
        ],
      },
    ],
  },
  {
    module: 2,
    title: "CSS Fundamentals",
    emoji: "🎨",
    topics: [
      {
        id: 3,
        topic: "CSS Basics",
        description: "Learn how to style HTML pages and improve the visual appearance of websites.",
        motivation: "Your page has style now! CSS transforms a plain document into a real, polished interface. 🖌️",
        resource: {
          name: "MDN — CSS Styling Basics",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Styling_basics",
        },
        task: "Style the Student Registration Form created in Module 1.",
        requirements: [
          "Apply a consistent color theme",
          "Use custom fonts",
          "Add spacing using margin and padding",
          "Style input fields",
          "Create attractive buttons",
          "Add rounded corners",
          "Apply box shadows",
          "Add hover effects",
          "Center the form on the page",
          "Ensure readability",
        ],
      },
      {
        id: 4,
        topic: "CSS Flexbox",
        description: "Learn how to build flexible and responsive layouts using Flexbox.",
        motivation: "Flexbox unlocked! You can now build any layout with confidence — this is a superpower every frontend dev needs. ⚡",
        resource: {
          name: "MDN — CSS Flexbox",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/CSS_layout/Flexbox",
        },
        task: "Create a responsive product listing page.",
        requirements: [
          "Navigation Bar",
          "Hero Banner",
          "Minimum 6 Product Cards",
          "Product Image on each card",
          "Product Name on each card",
          "Product Price on each card",
          "Short Description on each card",
          "Buy Now Button on each card",
          "Arrange cards using Flexbox",
          "Add hover effects",
        ],
      },
      {
        id: 5,
        topic: "CSS Grid",
        description: "Learn how to build complex layouts using CSS Grid.",
        motivation: "Grid mastered! You can now build magazine-style layouts that look incredible on any screen. 🗂️",
        resource: {
          name: "MDN — CSS Grid",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/CSS_layout/Grids",
        },
        task: "Create a modern photo gallery.",
        requirements: [
          "Minimum 12 Images",
          "Responsive Grid Layout",
          "Equal spacing between items",
          "Image captions",
          "Hover animation on images",
          "Responsive design across screen sizes",
        ],
      },
      {
        id: 6,
        topic: "Responsive Web Design",
        description: "Make websites adapt to different screen sizes using responsive design techniques.",
        motivation: "Your site works everywhere now! Responsive design is what separates hobbyist projects from professional ones. 📱",
        resource: {
          name: "MDN — Responsive Design",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/CSS_layout/Responsive_Design",
        },
        task: "Convert the Product Listing Page into a fully responsive website.",
        requirements: [
          "Desktop View",
          "Tablet View",
          "Mobile View",
          "Media Queries",
          "Responsive Images",
          "Flexible Layout",
          "Proper spacing across all devices",
        ],
      },
      {
        id: 7,
        topic: "CSS Animations & Transitions",
        description: "Enhance user experience by adding smooth animations and transitions.",
        motivation: "Motion design achieved! Animations make your UI feel alive and professional — recruiters love this! ✨",
        resource: {
          name: "MDN — CSS Transitions",
          url: "https://developer.mozilla.org/en-US/docs/Web/CSS/Guides/Transitions",
        },
        task: "Design an animated landing page.",
        requirements: [
          "Animated Hero Section",
          "Animated Buttons",
          "Hover Effects",
          "Smooth Section Transitions",
          "Card Animations",
          "Fade-in Elements",
          "Scale Effects",
          "Smooth Scrolling",
        ],
      },
    ],
  },
  {
    module: 3,
    title: "JavaScript Fundamentals",
    emoji: "⚡",
    topics: [
      {
        id: 8,
        topic: "JavaScript Basics",
        description: "Learn JavaScript fundamentals and interact with web pages.",
        motivation: "You can write code that thinks! JavaScript gives your pages a brain — incredible first step. 🧠",
        resource: {
          name: "MDN — JavaScript Scripting",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting",
        },
        task: "Build a Simple Calculator.",
        requirements: [
          "Addition",
          "Subtraction",
          "Multiplication",
          "Division",
          "Clear Button",
          "Input Validation",
          "Display Results",
          "User-friendly Interface",
        ],
      },
      {
        id: 9,
        topic: "DOM Manipulation",
        description: "Learn how JavaScript interacts with HTML elements using the DOM.",
        motivation: "DOM mastered! You can now dynamically control every element on a page — that's real power. 🚀",
        resource: {
          name: "MDN — DOM Scripting",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/DOM_scripting",
        },
        task: "Build a To-Do List Application.",
        requirements: [
          "Add Tasks",
          "Edit Tasks",
          "Delete Tasks",
          "Mark as Completed",
          "Task Counter",
          "Local Storage persistence",
          "Filter Completed Tasks",
          "Responsive UI",
        ],
      },
      {
        id: 10,
        topic: "JavaScript Events",
        description: "Understand JavaScript events and event handling.",
        motivation: "Events conquered! Your pages now respond to users in real time — this is interactive development! 🎯",
        resource: {
          name: "MDN — JavaScript Events",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/Events",
        },
        task: "Create an Image Slider.",
        requirements: [
          "Previous Button",
          "Next Button",
          "Auto Slide",
          "Pause on Hover",
          "Smooth Animation",
          "Navigation Dots",
          "Responsive Layout",
        ],
      },
      {
        id: 11,
        topic: "Fetch API",
        description: "Learn how to fetch data from external APIs and display it dynamically.",
        motivation: "API integration complete! You're now connecting your frontend to the real world — this is next-level! 🌐",
        resource: {
          name: "MDN — Network Requests (Fetch API)",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development/Core/Scripting/Network_requests",
        },
        task: "Build a Weather Dashboard.",
        requirements: [
          "Search by City",
          "Fetch Weather Data from an API",
          "Display Temperature",
          "Weather Icon",
          "Humidity",
          "Wind Speed",
          "Loading Indicator",
          "Error Handling for Invalid Cities",
        ],
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 12,
        topic: "Responsive Business Landing Page",
        description: "Apply everything learned throughout the MicroIntern to build a complete responsive website.",
        motivation: "Outstanding! You built a full-featured, responsive website from scratch. That's certificate-worthy. 🎉",
        resource: {
          name: "MDN — Web Development Guides",
          url: "https://developer.mozilla.org/en-US/docs/Learn_web_development",
        },
        task: "Choose one of the following and build a complete responsive landing page: Restaurant, Gym, Coffee Shop, Travel Agency, Startup, Portfolio, Hospital, or College.",
        requirements: [
          "Responsive Navigation",
          "Hero Section",
          "About Section",
          "Services Section",
          "Feature Cards",
          "Testimonials",
          "Pricing Section",
          "Contact Form",
          "Footer",
          "CSS Animations",
          "JavaScript Interactions",
          "Responsive Design (Mobile, Tablet, Desktop)",
          "Clean Folder Structure",
          "Well-documented README.md",
        ],
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const FRONTEND_TASKS = FRONTEND_MODULES.flatMap((m) => m.topics);
